import React, { useState, useCallback } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Capacitor } from '@capacitor/core';
import SettingRow from '../SettingRow/SettingRow';
import Backend from '../../../backend';
import { toast } from 'react-toastify';
import IconToast from '../../../components/NewToast';
import _ from 'lodash';

const WechatBindSetting = ({ intl, userDetails, updateUserDetails }) => {
  const [bindLoading, setBindLoading] = useState(false);

  const isBound = !!_.get(userDetails, 'wechatUnionId');

  const handleUnbind = useCallback(async () => {
    try {
      const res = await fetch(Backend.api('unbindWechat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const payload = await res.json();
      if (payload.success) {
        toast.info(IconToast('success', intl.formatMessage({ id: 'wechat-unbound-successfully' })), {
          className: 'new-toast'
        });
        updateUserDetails({ wechatUnionId: null });
      } else {
        toast.info(IconToast('error', intl.formatMessage({ id: 'wechat-unbind-failed' })), { className: 'new-toast' });
      }
    } catch (error) {
      console.error('WeChat unbind error:', error);
      toast.info(IconToast('error', intl.formatMessage({ id: 'wechat-unbind-failed' })), { className: 'new-toast' });
    }
  }, [intl, updateUserDetails]);

  const handleBind = useCallback(async () => {
    const { isWechatInstalled } = await import('../../../utils/wechat/share');
    const { CapacitorWechat } = await import('@capgo/capacitor-wechat');

    const installed = await isWechatInstalled();
    if (!installed) {
      toast.info(IconToast('error', intl.formatMessage({ id: 'wechat-not-installed' })), { className: 'new-toast' });
      return;
    }

    try {
      setBindLoading(true);
      const { code } = await CapacitorWechat.auth({
        scope: 'snsapi_userinfo',
        state: 'pfplus_bind'
      });

      const res = await fetch(Backend.api('bindWechat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ wechatCode: code })
      });
      const payload = await res.json();
      if (payload.success) {
        toast.info(IconToast('success', intl.formatMessage({ id: 'wechat-bound-successfully' })), {
          className: 'new-toast'
        });
        updateUserDetails({ wechatUnionId: payload.unionid });
      } else {
        toast.info(IconToast('error', intl.formatMessage({ id: payload.message || 'wechat-bind-failed' })), {
          className: 'new-toast'
        });
      }
    } catch (error) {
      console.error('WeChat bind error:', error);
      toast.info(IconToast('error', intl.formatMessage({ id: 'wechat-bind-failed' })), { className: 'new-toast' });
    } finally {
      setBindLoading(false);
    }
  }, [intl, updateUserDetails]);

  const isWeb = !Capacitor.isNativePlatform();

  const handleBindClick = useCallback(() => {
    if (isWeb) {
      toast.info(IconToast('info', intl.formatMessage({ id: 'wechat-bind-in-app' })), { className: 'new-toast' });
      return;
    }
    handleBind();
  }, [isWeb, intl, handleBind]);

  return (
    <SettingRow
      label={intl.formatMessage({ id: 'wechat-account' })}
      value={
        isBound ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
            <CheckCircleIcon sx={{ fontSize: 18 }} />
            {intl.formatMessage({ id: 'bound' })}
          </Box>
        ) : (
          intl.formatMessage({ id: 'not-bound' })
        )
      }
      action={
        <Button
          variant={isBound ? 'text' : 'outlined'}
          size="small"
          onClick={isBound ? handleUnbind : handleBindClick}
          color={isBound ? 'inherit' : 'primary'}
          disabled={bindLoading}
          sx={{ minWidth: 42, fontSize: 12, px: 1, py: 0.25, color: isBound ? 'text.secondary' : undefined }}
        >
          {bindLoading ? <CircularProgress size={16} /> : intl.formatMessage({ id: isBound ? 'unbind' : 'bind' })}
        </Button>
      }
    />
  );
};

export default WechatBindSetting;
